import _ from "lodash";
import { getColumn } from "../functions";
import { ColumnHeaderSearch } from "./ColumnHeaderSearch.component";

/**
 * Builds MUI DataGrid column defs whose headers embed a server-driven
 * {@link ColumnHeaderSearch} control (per-column search, plus optional sort).
 *
 * Shared by ServerDataGridTable and the Delegate list so every page presents an
 * identical column-search UX. Native sorting/filtering is turned off — the grid
 * only holds one server page, so search and sort are driven from the headers.
 *
 * Each `columnConfig` entry:
 *   { field, headerName?, minWidth?, type?, search?, statusOptions?,
 *     valueGetter?, valueFormatter?, renderCell? }
 * where `search` is the control kind ("text" | "status" | "date"); a field
 * omitting it renders a header with no input.
 *
 * @param {Array}  columnConfig
 * @param {object} searchModelRef   ref whose `.current` is the live search model
 *   (so a header can seed its control from the latest value without rebuilding
 *   the columns on every keystroke).
 * @param {Function} onSearchChange (field, value) => void
 * @param {number} filterResetKey   bump to remount the header inputs (clear-all).
 * @param {Array}  [sortModel]      [{ field, sort }] — pass to show sort state.
 * @param {Function} [onSortChange] (field) => void — pass to enable header sort.
 * @param {Array}  [defaultStatusOptions] fallback options for status columns.
 */
export function createSearchColumns({
  columnConfig,
  searchModelRef,
  onSearchChange,
  filterResetKey = 0,
  sortModel,
  onSortChange,
  defaultStatusOptions = [],
}) {
  return columnConfig.map((col) =>
    getColumn({
      field: col.field,
      headerName: col.headerName ?? _.startCase(col.field),
      minWidth: col.minWidth,
      type: col.type ?? "string",
      sortable: false,
      filterable: false,
      ...(col.valueGetter ? { valueGetter: col.valueGetter } : {}),
      ...(col.valueFormatter ? { valueFormatter: col.valueFormatter } : {}),
      ...(col.renderCell ? { renderCell: col.renderCell } : {}),
      renderHeader: (params) => {
        const activeSort = sortModel?.[0];
        const sortDirection =
          activeSort?.field === col.field ? activeSort.sort : undefined;
        return (
          <ColumnHeaderSearch
            key={`${col.field}-${filterResetKey}`}
            label={params.colDef.headerName}
            field={col.field}
            searchKind={col.search}
            initialValue={searchModelRef?.current?.[col.field]}
            statusOptions={col.statusOptions ?? defaultStatusOptions}
            onChange={onSearchChange}
            sortDirection={sortDirection}
            onSortChange={onSortChange}
          />
        );
      },
    })
  );
}
